import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Share2 } from 'lucide-react';
import toastManager from '../../utils/toastManager';

const ShareModal = ({ isOpen, onClose, productName, shareLink }) => {
    const handleCopyLink = async () => {
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(shareLink);
                toastManager.success('Link copied to clipboard!');
                return;
            }

            // Fallback for older browsers or when clipboard API is not available
            const textArea = document.createElement('textarea');
            textArea.value = shareLink;
            textArea.style.position = 'fixed';
            textArea.style.top = '0';
            textArea.style.left = '0';
            textArea.style.width = '2em';
            textArea.style.height = '2em';
            textArea.style.padding = '0';
            textArea.style.border = 'none';
            textArea.style.outline = 'none';
            textArea.style.boxShadow = 'none';
            textArea.style.background = 'transparent';
            textArea.style.fontSize = '16px';
            textArea.setAttribute('readonly', '');

            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            textArea.setSelectionRange(0, textArea.value.length);

            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (successful) {
                toastManager.success('Link copied to clipboard!');
            } else {
                throw new Error('Copy command failed');
            }
        } catch (error) {
            console.error('Copy failed:', error);
            toastManager.error('Failed to copy link. Please copy manually.');
        }
    };

    const handleWhatsApp = () => {
        const text = encodeURIComponent(`Check out ${productName} on PSH Store!`);
        const url = encodeURIComponent(shareLink);
        window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
    };

    const handleTwitter = () => {
        const text = encodeURIComponent(`Check out ${productName} on PSH Store!`);
        const url = encodeURIComponent(shareLink);
        window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
    };

    const handleFacebook = () => {
        const url = encodeURIComponent(shareLink);
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
    };

    const handleTelegram = () => {
        const text = encodeURIComponent(`Check out ${productName}!`);
        window.open(`https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${text}`, '_blank');
    };

    const shareOptions = [
        {
            name: 'Telegram',
            icon: Share2,
            onClick: handleTelegram,
            color: 'text-blue-500'
        },
        {
            name: 'WhatsApp',
            icon: Share2,
            onClick: handleWhatsApp,
            color: 'text-green-500'
        },
        {
            name: 'Twitter',
            icon: Share2,
            onClick: handleTwitter,
            color: 'text-blue-400'
        },
        {
            name: 'Facebook',
            icon: Share2,
            onClick: handleFacebook,
            color: 'text-blue-600'
        }
    ];

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
                        onClick={onClose}
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="fixed inset-0 z-[101] flex items-center justify-center p-4"
                        onClick={onClose}
                    >
                        <div
                            className="w-full max-w-md overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-900"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="relative border-b border-gray-100 p-6 pb-4 dark:border-gray-800">
                                <h2 className="text-2xl font-bold text-gray-950 text-center dark:text-white">Share Product</h2>
                                <button
                                    onClick={onClose}
                                    className="absolute top-6 right-6 rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Share Options Grid */}
                            <div className="p-6">
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    {shareOptions.map((option) => (
                                        <button
                                            key={option.name}
                                            onClick={option.onClick}
                                            className="group flex flex-col items-center justify-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-6 transition-all duration-200 hover:border-primary-200 hover:bg-primary-50 dark:border-gray-700 dark:bg-gray-800/50 dark:hover:bg-gray-800"
                                        >
                                            <option.icon size={32} className={`${option.color} group-hover:scale-110 transition-transform`} />
                                            <span className="text-sm font-medium text-gray-700 transition-colors group-hover:text-gray-950 dark:text-gray-300 dark:group-hover:text-white">
                                                {option.name}
                                            </span>
                                        </button>
                                    ))}
                                </div>

                                {/* Product Link Display */}
                                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Product Link:</p>
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm text-gray-700 flex-1 truncate font-mono dark:text-gray-300">
                                            {shareLink}
                                        </p>
                                        <button
                                            onClick={handleCopyLink}
                                            className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-white hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"
                                        >
                                            <Copy size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ShareModal;
